import { useEffect, useRef } from "react";
import "./i18n";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, landingFor, NEXT_KEY, useAuth } from "./lib/auth";
import { api } from "./lib/api";
import i18n from "./i18n";
import Layout from "./components/Layout";
import { Loading } from "./components/common";
import { SignIn, SignUp } from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Profile from "./pages/Profile";
import Home from "./pages/Home";
import Practice from "./pages/Practice";
import DogNew from "./pages/DogNew";
import DogDetail from "./pages/DogDetail";
import ProgrammeDetail from "./pages/ProgrammeDetail";
import VisitPage from "./pages/VisitPage";
import Library, { ProtocolDetail } from "./pages/Library";
import Invite from "./pages/Invite";
import { OwnerHome, OwnerDog, OwnerTask } from "./pages/Owner";
import TaskReview from "./pages/TaskReview";
import Workspace from "./pages/Workspace";
import AuditLog from "./pages/AuditLog";
import Landing from "./pages/Landing";
import Reports from "./pages/Reports";
import Help from "./pages/Help";
import { PublicCatalogue, PublicProduct, AppCatalogue, AppProduct } from "./pages/Catalogue";
import ContentAdmin, { ContentEditor } from "./pages/ContentAdmin";
import LaunchAdmin, { PaymentResult, Pricing } from "./pages/LaunchPages";
import { AdminCertificates, AdminConfirmations, AdminPages, CaseEditor, CasesManage, ExportPage, PublicCase, PublicCases, PublicPage } from "./pages/DocsPages";
import AdminAccess from "./pages/AdminAccess";
import Review, { Compare, HandoverPage } from "./pages/Review";
import { Timeline, Photos, Indicators, BeforeAfter } from "./pages/Longitudinal";
import { CalendarPage, MessagesPage, ConversationPage, NotificationsPage, OwnerPrint } from "./pages/DailyPages";
import DemoStart, { DemoModules } from "./pages/DemoStart";
import LearningHome, { CoursePage } from "./pages/Learning";
import LessonPage from "./pages/LessonPage";
import CheckPage, { SimPage } from "./pages/CheckPage";
import EditorHome, { VersionEditor, ReviewQueue, ResponseTimes } from "./pages/EditorPages";
import { PracticalPage, StartPractical, PracticalQueue, MentoringHome, MentorQueue, MentorRequestPage, MentorBooking } from "./pages/MentoringPages";

function AuthCallback() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    api.post("/auth/google", { session_id: sid, interface_language: i18n.language?.startsWith("ru") ? "ru" : "en" })
      .then(async ({ data }) => {
        const me = await signIn(data.token);
        const next = sessionStorage.getItem(NEXT_KEY);
        sessionStorage.removeItem(NEXT_KEY);
        navigate(me?.user?.role_choice && next ? next : next?.startsWith("/invite/") ? next : landingFor(me), { replace: true });
      })
      .catch(() => navigate("/login?google=failed", { replace: true }));
  }, [navigate, signIn]);
  return <Loading />;
}

const ROLE_ROUTES = { owner: /^\/(owner|profile|demo|messages|notifications)(\/|$)/, groomer: /./ };

function RequireAuth({ children, bare }) {
  const { me } = useAuth();
  const loc = useLocation();
  if (me === undefined) return <Loading />;
  if (!me) {
    sessionStorage.setItem(NEXT_KEY, loc.pathname + loc.search);
    return <Navigate to="/login" replace />;
  }
  if (!me.user.role_choice && !bare) return <Navigate to="/onboarding" replace />;
  if (!bare && !(ROLE_ROUTES[me.user.active_role] || ROLE_ROUTES.groomer).test(loc.pathname)) return <Navigate to="/owner" replace />;
  return bare ? children : <Layout>{children}</Layout>;
}

function Root() {
  const { me } = useAuth();
  if (me === undefined) return <Loading />;
  return me ? <Navigate to={landingFor(me)} replace /> : <Landing />;
}

function AppRoutes() {
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  const P = (el) => <RequireAuth>{el}</RequireAuth>;
  return (
    <Routes>
      <Route path="/" element={<Root />} />
      <Route path="/login" element={<SignIn />} />
      <Route path="/register" element={<SignUp />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/invite/:token" element={<Invite />} />
      <Route path="/demo" element={<DemoStart />} />
      <Route path="/reports" element={P(<Reports />)} />
      <Route path="/help" element={<Help />} />
      <Route path="/catalogue" element={<PublicCatalogue />} />
      <Route path="/catalogue/:id" element={<PublicProduct />} />
      <Route path="/products" element={P(<AppCatalogue />)} />
      <Route path="/products/:id" element={P(<AppProduct />)} />
      <Route path="/admin/content" element={P(<ContentAdmin />)} />
      <Route path="/admin/content/:typ/:id" element={P(<ContentEditor />)} />
      <Route path="/cases" element={<PublicCases />} />
      <Route path="/cases/manage" element={P(<CasesManage />)} />
      <Route path="/cases/edit/:id" element={P(<CaseEditor />)} />
      <Route path="/cases/:id" element={<PublicCase />} />
      <Route path="/about/:slug" element={<PublicPage />} />
      <Route path="/exports" element={P(<ExportPage />)} />
      <Route path="/admin/certificates" element={P(<AdminCertificates />)} />
      <Route path="/admin/confirmations" element={P(<AdminConfirmations />)} />
      <Route path="/admin/pages" element={P(<AdminPages />)} />
      <Route path="/admin/access" element={P(<AdminAccess />)} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment/success" element={P(<PaymentResult />)} />
      <Route path="/payment/cancel" element={P(<PaymentResult cancelled />)} />
      <Route path="/admin/launch" element={P(<LaunchAdmin />)} />
      <Route path="/demo/modules" element={P(<DemoModules />)} />
      <Route path="/onboarding" element={<RequireAuth bare><Onboarding /></RequireAuth>} />
      <Route path="/profile" element={P(<Profile />)} />
      <Route path="/home" element={P(<Home />)} />
      <Route path="/practice" element={P(<Practice />)} />
      <Route path="/dogs/new" element={P(<DogNew />)} />
      <Route path="/dogs/:id" element={P(<DogDetail />)} />
      <Route path="/programmes/:id" element={P(<ProgrammeDetail />)} />
      <Route path="/programmes/:id/review" element={P(<Review />)} />
      <Route path="/programmes/:id/compare" element={P(<Compare />)} />
      <Route path="/handovers/:id" element={P(<HandoverPage />)} />
      <Route path="/dogs/:id/timeline" element={P(<Timeline />)} />
      <Route path="/dogs/:id/photos" element={P(<Photos />)} />
      <Route path="/dogs/:id/indicators" element={P(<Indicators />)} />
      <Route path="/dogs/:id/before-after" element={P(<BeforeAfter />)} />
      <Route path="/calendar" element={P(<CalendarPage />)} />
      <Route path="/messages" element={P(<MessagesPage />)} />
      <Route path="/messages/:id" element={P(<ConversationPage />)} />
      <Route path="/notifications" element={P(<NotificationsPage />)} />
      <Route path="/owner/dogs/:id/print" element={P(<OwnerPrint />)} />
      <Route path="/visits/:id" element={P(<VisitPage />)} />
      <Route path="/tasks/:id" element={P(<TaskReview />)} />
      <Route path="/library" element={P(<Library />)} />
      <Route path="/library/:id" element={P(<ProtocolDetail />)} />
      <Route path="/workspace" element={P(<Workspace />)} />
      <Route path="/audit" element={P(<AuditLog />)} />
      <Route path="/learning" element={P(<LearningHome />)} />
      <Route path="/learning/courses/:id" element={P(<CoursePage />)} />
      <Route path="/learning/v/:vid/u/:key" element={P(<LessonPage />)} />
      <Route path="/learning/v/:vid/check/:key" element={P(<CheckPage />)} />
      <Route path="/learning/v/:vid/sim/:key" element={P(<SimPage />)} />
      <Route path="/learning/editor" element={P(<EditorHome />)} />
      <Route path="/learning/editor/versions/:vid" element={P(<VersionEditor />)} />
      <Route path="/learning/reviews" element={P(<ReviewQueue />)} />
      <Route path="/learning/v/:vid/practical/:key" element={P(<StartPractical />)} />
      <Route path="/learning/practicals/:id" element={P(<PracticalPage />)} />
      <Route path="/learning/practical-queue" element={P(<PracticalQueue />)} />
      <Route path="/mentoring" element={P(<MentoringHome />)} />
      <Route path="/mentoring/queue" element={P(<MentorQueue />)} />
      <Route path="/mentoring/book" element={P(<MentorBooking />)} />
      <Route path="/learning/editor/response-times" element={P(<ResponseTimes />)} />
      <Route path="/mentoring/:id" element={P(<MentorRequestPage />)} />
      <Route path="/owner" element={P(<OwnerHome />)} />
      <Route path="/owner/dogs/:id" element={P(<OwnerDog />)} />
      <Route path="/owner/tasks/:id" element={P(<OwnerTask />)} />
      <Route path="*" element={<Root />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
